/**
 * Attack Graph Icon Component
 *
 * Type-safe icon component for attack path nodes with:
 * - Error boundary for graceful failure handling
 * - Suspense for lazy-loaded icon sources
 * - Runtime validation of icon identifiers
 * - Backwards compatibility with legacy icons
 *
 * @example
 * ```tsx
 * // New prefixed format
 * <AttackGraphIcon icon="aws:Lambda" size={24} />
 * <AttackGraphIcon icon="lucide:server" />
 *
 * // Legacy format (still supported)
 * <AttackGraphIcon icon="Server" />
 *
 * // With fallback
 * <AttackGraphIcon icon="unknown" fallback={<CustomFallback />} />
 * ```
 */

// Import registry to ensure lucide renderer is registered
import './registry/lucide.registry';

import { HelpCircle } from 'lucide-react';
import type { ReactNode } from 'react';
import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { iconRenderers } from './registry';
import type { IconIdentifier } from './types';
import { parseIconIdentifier } from './types';

// ============================================================================
// Props Interface
// ============================================================================

export interface AttackGraphIconProps {
  /** Type-safe icon identifier (e.g., 'aws:Lambda', 'lucide:server', or legacy 'Server') */
  icon: IconIdentifier | string;
  /** Icon size in pixels */
  size?: number;
  /** CSS class name for styling */
  className?: string;
  /** Custom fallback when icon not found */
  fallback?: ReactNode;
  /** Accessibility label */
  'aria-label'?: string;
}

// ============================================================================
// Fallback Components
// ============================================================================

/**
 * Error boundary fallback shown when icon rendering fails.
 */
function IconFallback({ error }: { error: Error }) {
  console.error('Icon failed to render:', error);
  return <HelpCircle size={24} className="text-chariot-text-tertiary" />;
}

/**
 * Loading skeleton for Suspense boundary.
 */
function IconSkeleton({ size }: { size: number }) {
  return (
    <div
      className="bg-chariot-background-tertiary animate-pulse rounded"
      style={{ width: size, height: size }}
    />
  );
}

// ============================================================================
// Inner Component
// ============================================================================

/**
 * Inner icon component that handles rendering logic.
 */
function AttackGraphIconInner({
  icon,
  size = 24,
  className,
  fallback,
  'aria-label': ariaLabel,
}: AttackGraphIconProps) {
  // Handle empty/undefined - use default icon via legacy renderer
  if (!icon) {
    const legacyRenderer = iconRenderers.get('legacy');
    if (legacyRenderer) {
      return (
        <span role="img" aria-label={ariaLabel} className={className}>
          {legacyRenderer.render('', { size, className })}
        </span>
      );
    }
    // Ultimate fallback if renderer not available
    return <HelpCircle size={size} className={className} />;
  }

  // Try runtime validation for new format
  const parsed = parseIconIdentifier(icon);

  if (!parsed) {
    // Invalid format - fallback to legacy renderer
    const legacyRenderer = iconRenderers.get('legacy');
    if (legacyRenderer) {
      return (
        <span role="img" aria-label={ariaLabel} className={className}>
          {legacyRenderer.render(icon, { size, className })}
        </span>
      );
    }
    // Ultimate fallback if renderer not available
    return <HelpCircle size={size} className={className} />;
  }

  const { source, name } = parsed;
  const renderer = iconRenderers.get(source);

  // No renderer for this source - fallback to legacy
  if (!renderer || !renderer.validate(name)) {
    const legacyRenderer = iconRenderers.get('legacy');
    const fallbackContent = legacyRenderer ? (
      legacyRenderer.render(icon, { size, className })
    ) : (
      <HelpCircle size={size} className={className} />
    );
    return (
      <span role="img" aria-label={ariaLabel} className={className}>
        {fallback ?? fallbackContent}
      </span>
    );
  }

  // Render with Suspense for lazy-loaded icons
  return (
    <Suspense fallback={<IconSkeleton size={size} />}>
      <span role="img" aria-label={ariaLabel} className={className}>
        {renderer.render(name, { size, className })}
      </span>
    </Suspense>
  );
}

// ============================================================================
// Main Export
// ============================================================================

/**
 * Attack Graph Icon with Error Boundary.
 *
 * Provides graceful error handling for icon rendering failures,
 * which can occur with external icon packages.
 */
export function AttackGraphIcon(props: AttackGraphIconProps) {
  return (
    <ErrorBoundary FallbackComponent={IconFallback}>
      <AttackGraphIconInner {...props} />
    </ErrorBoundary>
  );
}
